import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSocket } from './useSocket';
import { baseApi } from '../api/baseApi';

/**
 * useRealtime — subscribe to Socket.IO events for the current view
 *
 * Auto-joins the relevant room on mount, leaves on unmount.
 * Maps incoming socket events to RTK Query cache invalidations
 * so the UI stays in sync without manual refetching.
 *
 * Usage:
 *   // In ProjectView — auto-join project room, listen for task updates
 *   useRealtime({
 *     room: { type: 'project', id: projectId },
 *     events: {
 *       'task:created': (data) => console.log('New task:', data),
 *       'task:updated': (data) => console.log('Task changed:', data),
 *       'kanban:card_moved': (data) => console.log('Card moved:', data),
 *     },
 *     invalidate: ['Task', 'Project'],  // RTK Query tags to invalidate on any event
 *   });
 *
 *   // In HiveLink channel — auto-join channel room, listen for messages
 *   useRealtime({
 *     room: { type: 'channel', id: channelId },
 *     events: {
 *       'hivelink:message:new': (data) => addMessage(data),
 *       'hivelink:typing:start': (data) => setTyping(data.userId, true),
 *       'hivelink:typing:stop': (data) => setTyping(data.userId, false),
 *     },
 *   });
 */
export const useRealtime = ({ room, events = {}, invalidate = [], enabled = true }) => {
  const { on, off, joinRoom, leaveRoom, connected } = useSocket();
  const dispatch = useDispatch();
  const handlersRef = useRef(events);

  // Keep handlers ref current (avoid stale closures)
  useEffect(() => {
    handlersRef.current = events;
  }, [events]);

  // Join/leave room
  useEffect(() => {
    if (!enabled || !room?.type || !room?.id || !connected) return;

    joinRoom(room.type, room.id);

    return () => {
      leaveRoom(room.type, room.id);
    };
  }, [room?.type, room?.id, connected, enabled, joinRoom, leaveRoom]);

  // Subscribe to events
  useEffect(() => {
    if (!enabled || !connected) return;

    const cleanups = [];

    Object.entries(handlersRef.current).forEach(([eventName, handler]) => {
      const wrappedHandler = (data) => {
        // Call the user's handler
        handler?.(data);

        // Auto-invalidate RTK Query cache
        if (invalidate.length > 0) {
          invalidate.forEach((tag) => {
            dispatch(baseApi.util.invalidateTags([tag]));
          });
        }
      };

      const cleanup = on(eventName, wrappedHandler);
      cleanups.push(() => {
        if (cleanup) cleanup();
        else off(eventName, wrappedHandler);
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [connected, enabled, on, off, dispatch, invalidate]);
};

/**
 * useRealtimeTask — convenience hook for task-related real-time events
 * Auto-joins project room and listens for task CRUD events
 */
export const useRealtimeTask = (projectId, callbacks = {}) => {
  useRealtime({
    room: { type: 'project', id: projectId },
    events: {
      'task:created': callbacks.onTaskCreated,
      'task:updated': callbacks.onTaskUpdated,
      'task:deleted': callbacks.onTaskDeleted,
      'kanban:card_moved': callbacks.onCardMoved,
      'sprint:updated': callbacks.onSprintUpdated,
    },
    invalidate: ['Task', 'TaskStats'],
    enabled: !!projectId,
  });
};

/**
 * useRealtimeChannel — convenience hook for HiveLink channel events
 */
export const useRealtimeChannel = (channelId, callbacks = {}) => {
  useRealtime({
    room: { type: 'channel', id: channelId },
    events: {
      'hivelink:message:new': callbacks.onMessage,
      'hivelink:message:edited': callbacks.onMessageEdited,
      'hivelink:message:deleted': callbacks.onMessageDeleted,
      'hivelink:message:reaction': callbacks.onReaction,
      'hivelink:typing:start': callbacks.onTypingStart,
      'hivelink:typing:stop': callbacks.onTypingStop,
      'hivelink:presence:update': callbacks.onPresenceUpdate,
    },
    invalidate: [],
    enabled: !!channelId,
  });
};

/**
 * useRealtimeNotification — listen for global notifications
 * (workspace-scoped, no room needed)
 */
export const useRealtimeNotification = (callback) => {
  const { on, off, connected } = useSocket();

  useEffect(() => {
    if (!connected || !callback) return;

    const cleanup = on('notification:new', callback);
    return () => {
      if (cleanup) cleanup();
      else off('notification:new', callback);
    };
  }, [connected, callback, on, off]);
};

export default useRealtime;
